import { Component } from '@theme/component';

/**
 * Upgrades the "Încarcă mai multe" link into an in-place loader (SPEC §6.8).
 *
 * This component deliberately does not fetch anything. The section that owns the
 * list knows how to render its own next page — collection, search and blog all
 * do it differently — so this emits an event, marks the button busy, and owns
 * exactly one thing the sections keep forgetting: moving focus to the first new
 * item once the page has landed. Without that, a keyboard user who activates
 * "load more" is returned to the top of the document and has to traverse
 * everything they already read.
 *
 * A consumer listens for `pagination-progress:load` and calls
 * `event.detail.complete(container)` with the element it appended into, or
 * `event.detail.complete()` if the load failed — which restores the button
 * rather than leaving it permanently busy.
 *
 * With no listener, `preventDefault` never runs and the link navigates normally.
 * That is the intended no-JS behaviour, not a fallback.
 *
 * @extends {Component<{ button: HTMLAnchorElement }>}
 */
class PaginationProgress extends Component {
  requiredRefs = ['button'];

  /**
   * @param {MouseEvent} event
   */
  handleClick(event) {
    // Let modified clicks (new tab, new window, download) behave as links.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const { button } = this.refs;

    const detail = {
      url: button.href,
      /** @param {Element} [container] The element the new items were appended into. */
      complete: (container) => {
        this.#setBusy(false);
        if (container) this.#focusFirstNewItem(container);
      },
    };

    const loadEvent = new CustomEvent('pagination-progress:load', {
      bubbles: true,
      cancelable: true,
      detail,
    });

    // A listener claims the click by cancelling the event. Nothing claimed it
    // means nothing is going to load in place, so the link must navigate.
    const claimed = !this.dispatchEvent(loadEvent);
    if (!claimed) return;

    event.preventDefault();
    this.#setBusy(true);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const { button } = this.refs;
    button.setAttribute('aria-busy', String(busy));
  }

  /**
   * Moves focus to the first item of the page that just loaded.
   *
   * Prefers a real focusable descendant — a card's own link — so focus lands
   * somewhere the user can act on. Falls back to making the item itself
   * programmatically focusable, and removes that tabindex again on blur so the
   * DOM is not left with stray tab stops after several loads.
   *
   * @param {Element} container
   */
  #focusFirstNewItem(container) {
    const target =
      container.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? container;

    if (!(target instanceof HTMLElement)) return;

    if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea')) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }

    target.focus({ preventScroll: false });
  }
}

if (!customElements.get('pagination-progress')) {
  customElements.define('pagination-progress', PaginationProgress);
}
